import React from "react";
import { Link } from "react-router-dom";

export default function About() {
  return (
    <main data-testid="about-page" className="container-edit pt-16 md:pt-24 pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-7">
          <p className="text-xs uppercase tracking-[0.3em] text-brand-terracotta mb-5">
            About the Founder
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-brand-forest leading-[0.95]">
            A nursery nurse,<br />a mother,<br />a <em className="text-brand-moss not-italic font-light">steady</em> ally.
          </h1>
          <div className="mt-10 space-y-6 text-brand-charcoal leading-relaxed">
            <p>
              I trained as an NNEB nursery nurse decades ago, when the qualification
              still meant a long, careful apprenticeship in the science of small
              children — how they regulate, how they break, how they rebuild. From
              there I spent decades working within family dynamics and family-support
              settings, sitting alongside parents through the hardest hours of their
              week.
            </p>
            <p>
              I am also a mother who has lived the family-court system from the
              inside. Nothing in my training had prepared me for the change in my
              children's behaviour around contact — the panic-lying, the
              hyper-vigilance, the anxiety threaded through every handover, the
              quiet pressure they carried in their bodies around the weekly
              transitions. So I built the framework I never had.
            </p>
            <p>
              That framework is part decades of family-support practice, part
              trauma-informed knowledge, and part what only a mother who has lived
              this can know — the small, court-safe, repeatable ways of holding a
              child through the week so they slowly stop bracing for it. It is
              gentle, observable, and never asks you to coach, prompt, or
              escalate. It works because it is ordinary on purpose — and ordinary,
              done consistently, is what eventually rebuilds a child's nervous
              system between two homes.
            </p>
            <p>
              That is what I now offer privately to other mothers. Quietly.
              Without escalation. Without ever asking you to do anything that
              could be twisted into a claim of alienation.
            </p>
          </div>

          <Link
            to="/inquire"
            data-testid="about-cta"
            className="mt-12 inline-flex items-center px-8 py-4 bg-brand-forest text-brand-bg text-sm tracking-wide hover:bg-brand-moss transition-colors rounded-sm"
          >
            Inquire about 1:1 work
          </Link>
        </div>

        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <div className="border border-brand-border bg-brand-surface">
            <img
              src="https://images.pexels.com/photos/3933249/pexels-photo-3933249.jpeg"
              alt="A calm, sunlit family room — peaceful and on-tone with the brand"
              className="w-full aspect-[3/4] object-cover"
            />
          </div>
          <div className="mt-6 border-t border-brand-border pt-6 text-sm space-y-3 text-brand-muted leading-relaxed">
            <p><strong className="text-brand-forest">Qualifications:</strong> NNEB-trained Nursery Nurse.</p>
            <p><strong className="text-brand-forest">Experience:</strong> decades in early-years practice.</p>
            <p><strong className="text-brand-forest">Specialism:</strong> trauma-informed, court-safe support for protective mothers.</p>
            <p><strong className="text-brand-forest">Independence:</strong> not affiliated with any court, CAFCASS officer, or legal practice.</p>
          </div>
        </div>
      </div>

      {/* PRINCIPLES */}
      <section className="mt-32 border-t border-brand-border pt-20">
        <p className="text-xs uppercase tracking-[0.3em] text-brand-muted mb-6">The Four Principles</p>
        <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-brand-forest leading-tight max-w-3xl">
          What every piece of work here is built on.
        </h2>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-12">
          {[
            {
              n: "01",
              title: "Court-safe by default.",
              body: "Nothing you do under this framework can be reasonably misread as alienation. We document; we never coach.",
            },
            {
              n: "02",
              title: "Read the body before the words.",
              body: "Post-contact behaviour is data. Decoding it is half the work — and the half that prevents misinterpretation.",
            },
            {
              n: "03",
              title: "Your home as quiet counter-balance.",
              body: "We do not fight the other side. We make your household so reliably steady that the contrast becomes the evidence.",
            },
            {
              n: "04",
              title: "Break the cycle, not the child.",
              body: "The generational patterns are what we interrupt — never the child carrying them. Cycles end when children are met with steadiness, not when they're asked to choose sides.",
            },
          ].map((p) => (
            <div key={p.n} className="flex gap-6">
              <span className="font-serif text-3xl text-brand-terracotta">{p.n}</span>
              <div>
                <h3 className="font-serif text-2xl text-brand-forest mb-3">{p.title}</h3>
                <p className="text-brand-charcoal leading-relaxed">{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
